<template>
  <template v-if="authStore.loading">
    <AppContentLoader />
  </template>

  <template v-else>
    <RouterView />
  </template>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { onMounted, onUnmounted, watch } from 'vue';

import { AppContentLoader } from '@/components/app';

import config from '@/config';
import { usePageTitleStore, useAuthStore } from '@/store';

const pageTitleStore = usePageTitleStore();
const authStore = useAuthStore();

const { pageTitle } = storeToRefs(pageTitleStore);

// get scrollbar width here so we know how much padding to add
let cachedScrollbarWidth = 0;

const getBrowserScrollbarWidth = () => {
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  outer.style.msOverflowStyle = 'scrollbar';
  document.body.appendChild(outer);
  const inner = document.createElement('div');
  outer.appendChild(inner);
  const scrollbarWidth = (outer.offsetWidth - inner.offsetWidth);
  outer.parentNode.removeChild(outer);
  return scrollbarWidth;
};

const lockScrollingWhenModalOpen = () => {
  const htmlNode = document.documentElement;
  const bodyNode = document.body;

  const hasAppModal = document.querySelectorAll('dialog.modal[open]').length > 0;
  const hasTinyMceModal = document.querySelectorAll('.tox-dialog-wrap').length > 0;
  const shouldLock = hasAppModal || hasTinyMceModal;

  if (shouldLock) {
    if (cachedScrollbarWidth > 0) {
      htmlNode.style.paddingRight = `${cachedScrollbarWidth}px`;
    }
    bodyNode.style.overflow = 'hidden';
  } else {
    htmlNode.style.paddingRight = '';
    bodyNode.style.overflow = '';
  }
};

let observer = null;

onMounted(() => {
  // calculate width before starting observer
  cachedScrollbarWidth = getBrowserScrollbarWidth();

  observer = new MutationObserver(() => lockScrollingWhenModalOpen());
  observer.observe(document.body, { childList: true, subtree: true });
});

onUnmounted(() => {
  if (observer) observer.disconnect();
  // Cleanup
  document.documentElement.style.paddingRight = '';
  document.body.style.overflow = '';
});

watch(
  pageTitle,
  () => {
    document.title = pageTitle.value;
  },
);

watch(() => authStore.user, async (user) => {
  if (!user || typeof window === 'undefined' || !config.clarityProjectId) {
    return;
  }

  try {
    const { default: Clarity } = await import('@microsoft/clarity');

    // Use user email as custom ID
    Clarity.identify(user.email);

    // Create custom tags to filter by in Clarity Dashboard
    Clarity.setTag('Name', `${user.firstName} ${user.lastName}`);
    Clarity.setTag('Email', user.email);
  } catch (error) {
    console.error('[Clarity] Identity error: ', error);
  }
}, { immediate: true });
</script>
